import React, { useEffect, useCallback, useRef, useState } from 'react';
import GlobalStyles from './styles/GlobalStyles';
import { BackgroundWrapper, LandingPage } from './App.styled';
import Header from './components/ui/Header';
import CombinedScene from './components/scenes/CombinedScene';
import Menu from './components/ui/Menu';
import CorporateContent from './components/ui/CorporateContent';
import PersistentCanvas from './components/utils/PersistentCanvas';
import PerformanceMonitor from './components/utils/PerformanceMonitor';
import Preloader from './components/utils/Preloader';
import QuestionSequence from './components/ui/QuestionSequence';
import MicrophonePermission from './components/ui/MicrophonePermission';
import MicrophonePermissionUI from './components/ui/MicrophonePermissionUI';
import NewspaperModal from './components/ui/NewspaperModal';
import Subtitles from './components/ui/Subtitles';
import SEOContent from './components/ui/SEOContent';
import { INTRO_CONFIG } from './config/questionConfig';
import { SiteProvider, useActiveScreen, useCorporateMode, usePreloading, useSound } from './contexts/SiteContext';
import { CanvasProvider, useCanvasContext } from './contexts/CanvasContext';
import { MicrophoneProvider, useMicrophone } from './contexts/MicrophoneContext';
import { logger } from './utils/logger';
import { trackEvent } from './utils/analytics';
import useHangup from './hooks/useHangup';
import imagePreloader from './utils/imagePreloader';

// CombinedScene now handles both CityScene and PhoneboxScene

const SceneController = React.memo(() => {
  const { phoneboxState, setPhoneOffHook, resetPhoneboxState, tvState, setTvZoomed } = useCanvasContext();
  const { activeScreen, setActiveScreen } = useActiveScreen();
  const hasInitializedRef = useRef(false);
  const screenGuardHangup = useHangup({ source: 'screen_guard' });
  const animationHangup = useHangup({ source: 'animation_complete' });
  const phoneboxClickHangup = useHangup({ source: 'phonebox_click' });

  // Reset phonebox state and active screen when component mounts (canvas remount)
  useEffect(() => {
    // Only reset on first mount to prevent duplicate resets
    if (!hasInitializedRef.current) {
      resetPhoneboxState();
      setActiveScreen(null, { source: 'scene_reset' }); // Reset to logos/default view
      logger.scene('Canvas remounted - reset phonebox state and active screen');
      hasInitializedRef.current = true;
    }
  }, [resetPhoneboxState, setActiveScreen]);

  // Reset phone state when switching away from phone screen - optimized to prevent mid-transition re-renders
  useEffect(() => {
    if (activeScreen !== 'phone' && phoneboxState.isOffHook) {
      // Only reset if actually off hook to prevent unnecessary state changes
      screenGuardHangup({ reason: 'auto_reset' });
    }
  }, [activeScreen, phoneboxState.isOffHook, screenGuardHangup]);

  // Reset TV zoom state when switching away from TV screen
  useEffect(() => {
    if (activeScreen !== 'tv' && tvState.isZoomed) {
      // Only reset if actually zoomed to prevent unnecessary state changes
      setTvZoomed(false, { source: 'screen_guard', reason: 'auto_reset' });
    }
  }, [activeScreen, tvState.isZoomed, setTvZoomed]);

  const handlePhoneboxClick = useCallback(() => {
    const nextState = !phoneboxState.isOffHook;
    
    if (nextState) {
      // Pick up phone - only allow if phone is fully on hook (not transitioning)
      const isFullyOnHook = !phoneboxState.isOffHook && phoneboxState.animationState === 'idle';
      if (!isFullyOnHook) {
        logger.debug('Phone not fully on hook - ignoring pickup attempt', {
          isOffHook: phoneboxState.isOffHook,
          animationState: phoneboxState.animationState
        });
        return;
      }
      // Pick up phone
      setPhoneOffHook(true, {
        source: 'phonebox_click',
        reason: 'pickup'
      });
    } else {
      // Hang up phone - always allow hangup (no debounce)
      phoneboxClickHangup({ reason: 'manual_hangup' });
    }
  }, [phoneboxState.isOffHook, phoneboxState.animationState, setPhoneOffHook, phoneboxClickHangup]);

  const handleAnimationComplete = useCallback(() => {
    // Animation is complete, return phone to hook
    animationHangup({ reason: 'auto_reset' });
  }, [animationHangup]);

  return (
    <PersistentCanvas>
      <CombinedScene 
        onSceneClick={handlePhoneboxClick} 
        onAnimationComplete={handleAnimationComplete} 
      />
    </PersistentCanvas>
  );
});

const AppContent = () => {
  const { activeScreen, setActiveScreen } = useActiveScreen();
  const { isCorporateMode } = useCorporateMode();
  const { isPreloading } = usePreloading();
  
  // Debug flag: show canvas during preload (via URL parameter ?debugPreload=true)
  // Only works in development mode
  const showCanvasDuringPreloadRef = useRef(false);
  const showDebugMessageRef = useRef(false);
  
  useEffect(() => {
    // Only enable debug mode in development
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (!isDevelopment) {
      return;
    }
    
    const urlParams = new URLSearchParams(window.location.search);
    const debugPreload = urlParams.get('debugPreload') === 'true';
    showCanvasDuringPreloadRef.current = debugPreload;
    showDebugMessageRef.current = debugPreload;
    if (debugPreload) {
      console.log('🔍 Debug mode: Canvas will be visible during preload');
    }
  }, []);
  
  // Hide debug message when proceed button appears
  useEffect(() => {
    if (!showCanvasDuringPreloadRef.current || !isPreloading) {
      return;
    }
    
    const checkForButton = () => {
      const proceedButton = document.querySelector('[data-preloader-button="true"]');
      if (proceedButton && proceedButton.offsetParent !== null) {
        // Button is visible
        showDebugMessageRef.current = false;
      }
    };
    
    // Check immediately and then periodically
    checkForButton();
    const interval = setInterval(checkForButton, 100);
    
    return () => clearInterval(interval);
  }, [isPreloading]);
  const { phoneboxState, setPhoneOffHook, setIsQuestionDebugEnabled, newspaperModalOpen, closeNewspaperModal, openNewspaperModal, tvState, setTvZoomed } = useCanvasContext();
  const { permissionState, stream } = useMicrophone();
  const [questionSequenceEnabled, setQuestionSequenceEnabled] = useState(true);
  const [showMicrophoneUI, setShowMicrophoneUI] = useState(false);
  const [showIntroSubtitle, setShowIntroSubtitle] = useState(false);
  const introSubtitleTimeoutRef = useRef(null);
  const newspaperModalLastCloseRef = useRef(0);
  const { isSoundOn, toggleSound } = useSound();
  
  // Hangup hooks for different contexts
  const questionHangup = useHangup({ source: 'question_sequence' });
  const keyboardHangup = useHangup({ source: 'keyboard' });

  // Note: Phone audio ended event is now handled by Phonebox.jsx component
  // to avoid conflicts with the transition flow

  const handleQuestionComplete = useCallback((responses) => {
    logger.info('Question sequence completed with responses:', responses);
    // Here you could send responses to a server
    // For now, we'll just hang up the phone
    questionHangup({ reason: 'complete' });
  }, [questionHangup]);

  const handleQuestionHangup = useCallback(() => {
    logger.info('Question sequence hung up by user');
    questionHangup({ reason: 'hangup' });
  }, [questionHangup]);


  const handleMicrophonePermissionGranted = useCallback(() => {
    logger.info('Microphone permission granted');
    trackEvent('microphone_permission', {
      event_category: 'microphone',
      event_label: 'granted'
    });
  }, []);

  // Track intro audio start and show subtitle after 3 seconds (or immediately if sound is off)
  useEffect(() => {
    const shouldShowIntro = !isCorporateMode && 
      activeScreen === 'phone' && 
      phoneboxState.isOffHook && 
      phoneboxState.isPlaying && 
      !phoneboxState.isInQuestionMode;
    
    if (shouldShowIntro) {
      // Clear any existing timeout
      if (introSubtitleTimeoutRef.current) {
        clearTimeout(introSubtitleTimeoutRef.current);
      }
      
      // Reset subtitle visibility
      setShowIntroSubtitle(false);
      
      // Show subtitle immediately if sound is off, otherwise after 12 seconds
      const delay = isSoundOn ? 12000 : 0;
      introSubtitleTimeoutRef.current = setTimeout(() => {
        setShowIntroSubtitle(true);
      }, delay);
    } else {
      // Clear timeout and hide subtitle if conditions no longer met
      if (introSubtitleTimeoutRef.current) {
        clearTimeout(introSubtitleTimeoutRef.current);
        introSubtitleTimeoutRef.current = null;
      }
      setShowIntroSubtitle(false);
    }
    
    // Cleanup on unmount
    return () => {
      if (introSubtitleTimeoutRef.current) {
        clearTimeout(introSubtitleTimeoutRef.current);
      }
    };
  }, [isCorporateMode, activeScreen, phoneboxState.isOffHook, phoneboxState.isPlaying, phoneboxState.isInQuestionMode, isSoundOn]);

  const handleMicrophonePermissionDenied = useCallback((error) => {
    logger.warn('Microphone permission denied:', error);
    trackEvent('microphone_permission', {
      event_category: 'microphone',
      event_label: 'denied',
      error_type: error?.name || 'unknown'
    });
  }, []);

  const handleQuestionSequenceToggle = useCallback((enabled) => {
    logger.debug('Question sequence toggled:', enabled);
    setQuestionSequenceEnabled(enabled);
    setIsQuestionDebugEnabled(enabled);
  }, [setIsQuestionDebugEnabled]);

  // Initialize debug state on mount to ensure synchronization
  useEffect(() => {
    setIsQuestionDebugEnabled(questionSequenceEnabled);
  }, [setIsQuestionDebugEnabled, questionSequenceEnabled]);

  // Handle delayed showing of microphone UI to allow permission check to complete
  useEffect(() => {
    if (phoneboxState.isOffHook && !phoneboxState.isInQuestionMode) {
      // Check if we need to show the UI (permission not granted or no stream)
      const needsUI = permissionState === 'unknown' || permissionState === 'prompt' || permissionState === 'denied' || !stream;
      
      if (needsUI) {
        // Add a delay before showing the UI to allow permission check to complete
        const timer = setTimeout(() => {
          // Double-check the conditions after the delay
          const stillNeedsUI = permissionState === 'unknown' || permissionState === 'prompt' || permissionState === 'denied' || !stream;
          setShowMicrophoneUI(stillNeedsUI);
        }, 1000); // 1 second delay
        
        return () => clearTimeout(timer);
      } else {
        // Permission is granted and we have a stream, don't show UI
        setShowMicrophoneUI(false);
      }
    } else {
      // Phone is on hook or in question mode, don't show UI
      setShowMicrophoneUI(false);
    }
  }, [phoneboxState.isOffHook, phoneboxState.isInQuestionMode, permissionState, stream]);

  const rotateScreen = useCallback((direction, metadata = {}) => {
    const { source = 'keyboard_navigation', ...restMetadata } = metadata;
    const screens = [null, 'phone', 'news', 'tv'];
    const currentIndex = screens.indexOf(activeScreen);

    if (direction === 'backward') {
      if (currentIndex === -1) {
        setActiveScreen('tv', { source, ...restMetadata });
      } else {
        const prevIndex = (currentIndex - 1 + screens.length) % screens.length;
        setActiveScreen(screens[prevIndex], { source, ...restMetadata });
      }
    } else if (direction === 'forward') {
      if (currentIndex === -1) {
        setActiveScreen('phone', { source, ...restMetadata });
      } else {
        const nextIndex = (currentIndex + 1) % screens.length;
        setActiveScreen(screens[nextIndex], { source, ...restMetadata });
      }
    }
  }, [activeScreen, setActiveScreen]);

  // Preload newspaper image when user is on the news screen
  // This ensures the image is ready when the modal is triggered
  useEffect(() => {
    if (activeScreen === 'news' && !isCorporateMode) {
      const preloadNewspaperImage = async () => {
        try {
          const imageSrc = '/images/medium_quality/newspaper.jpg';
          
          // Check if already cached
          if (imagePreloader.isCached(imageSrc)) {
            logger.debug('✅ Newspaper image already cached (user on news screen)');
            return;
          }

          // Preload the image in the background
          logger.debug('📥 Preloading newspaper image (user on news screen)...');
          await imagePreloader.preload(imageSrc);
          logger.debug('✅ Newspaper image preloaded (ready for modal)');
        } catch (error) {
          console.warn('⚠️ Failed to preload newspaper image:', error);
          // Don't throw - this is a background preload, modal will handle errors
        }
      };
      
      preloadNewspaperImage();
    }
  }, [activeScreen, isCorporateMode]);

  // Handle arrow key navigation to cycle through camera screens
  useEffect(() => {
    if (isCorporateMode || isPreloading) {
      return; // Don't handle keyboard navigation in corporate mode or during preloading
    }

    const handleKeyDown = (event) => {
      // Don't handle keys if user is typing in an input field
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable
      ) {
        return;
      }

      // Escape key: zoom TV out, hang up phone, and close newspaper modal
      if (event.key === 'Escape') {
        // If newspaper modal is open, let it handle escape (it has its own animation)
        // Don't preventDefault here so the modal's handler can work
        if (newspaperModalOpen) {
          // The modal will handle the escape key and call closeNewspaperModal when animation completes
          // We'll track the close time in a wrapper function
          return;
        }
        
        event.preventDefault();
        // Zoom TV out if zoomed
        if (tvState.isZoomed) {
          setTvZoomed(false, { source: 'keyboard', reason: 'escape_key' });
        }
        // Hang up phone if off hook
        if (phoneboxState.isOffHook) {
          keyboardHangup({ reason: 'escape_key' });
        }
        return;
      }

      // Enter/Space keys: trigger phone pickup, TV zoom, or open newspaper modal
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'Space' ||
        event.key === 'Spacebar'
      ) {
        event.preventDefault();
        // Pick up phone if on phone screen and not already off hook
        if (activeScreen === 'phone' && !phoneboxState.isOffHook) {
          setPhoneOffHook(true, { source: 'keyboard', reason: 'enter_key' });
        }
        // Zoom TV in if on TV screen and not already zoomed
        if (activeScreen === 'tv' && !tvState.isZoomed) {
          setTvZoomed(true, { source: 'keyboard', reason: 'enter_key' });
        }
        // Open newspaper modal if on news screen and modal not already open
        // Add cooldown to prevent rapid open/close cycles (850ms to account for 800ms closing animation)
        if (activeScreen === 'news' && !newspaperModalOpen) {
          const timeSinceLastClose = Date.now() - newspaperModalLastCloseRef.current;
          if (timeSinceLastClose > 850) {
            openNewspaperModal();
          }
        }
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        // Move backward (counter-clockwise: 0° → 270° → 180° → 90° → 0°)
        rotateScreen('backward', { source: 'keyboard_navigation' });
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        // Move forward (clockwise: 0° → 90° → 180° → 270° → 360°/0°)
        rotateScreen('forward', { source: 'keyboard_navigation' });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [rotateScreen, isCorporateMode, isPreloading, tvState.isZoomed, setTvZoomed, phoneboxState.isOffHook, keyboardHangup, newspaperModalOpen, openNewspaperModal, closeNewspaperModal, activeScreen, setPhoneOffHook]);

  // Enable swipe navigation on touch devices to mirror arrow keys
  useEffect(() => {
    if (isCorporateMode || isPreloading) {
      return;
    }

    let touchStartX = null;
    let touchStartY = null;
    let touchStartTime = 0;

    const resetTouchState = () => {
      touchStartX = null;
      touchStartY = null;
      touchStartTime = 0;
    };

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartTime = Date.now();
    };

    const handleTouchEnd = (event) => {
      if (touchStartX === null || touchStartY === null) {
        return;
      }

      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      const elapsed = Date.now() - touchStartTime;

      resetTouchState();

      const HORIZONTAL_THRESHOLD = 40;
      const MAX_DURATION = 800;

      if (elapsed > MAX_DURATION) {
        return;
      }

      if (Math.abs(deltaX) < HORIZONTAL_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) {
        return;
      }

      if (deltaX < 0) {
        // Swipe left -> same as ArrowRight (backward/counter-clockwise)
        rotateScreen('backward', { source: 'touch_navigation', gesture: 'swipe_left' });
      } else {
        // Swipe right -> same as ArrowLeft (forward/clockwise)
        rotateScreen('forward', { source: 'touch_navigation', gesture: 'swipe_right' });
      }
    };

    const handleTouchCancel = () => resetTouchState();

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchCancel);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [rotateScreen, isCorporateMode, isPreloading]);

  const renderMainContent = useCallback(() => {
    // Don't render corporate content during preloading to prevent overflow
    if (isCorporateMode && !isPreloading) {
      return <CorporateContent />;
    }
    
    // All content is now handled in 3D canvas - no 2D overlays needed
    return null;
  }, [isCorporateMode, isPreloading]);

  return (
    <>
      <GlobalStyles $isCorporateMode={isCorporateMode} />
      <style>
        {`
          @keyframes slideDown {
            from {
              transform: translateX(-50%) translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateX(-50%) translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
      <BackgroundWrapper $isCorporateMode={isCorporateMode}>
        {/* Always render 3D canvas - even during preloading */}
        {!isCorporateMode && <SceneController />}
        <LandingPage $isCorporateMode={isCorporateMode}>
          <SEOContent />
          <Header />
          {renderMainContent()}
          <Menu />
        </LandingPage>
        {/* Intro Subtitles - Show 3 seconds after intro audio starts playing */}
        {showIntroSubtitle && (
          <Subtitles
            subtitleText={INTRO_CONFIG.text}
            subtitleTimeouts={INTRO_CONFIG.subtitleTimeouts}
          />
        )}
        
        {/* Question Sequence Overlay - Only when in question mode */}
        {phoneboxState.isInQuestionMode && !isCorporateMode && (
          <QuestionSequence 
            onComplete={handleQuestionComplete}
            onHangup={handleQuestionHangup}
            showUI={phoneboxState.isQuestionDebugEnabled}
          />
        )}
        
        {/* Microphone Permission Request - Always active, even during preloading */}
        {!isCorporateMode && (
          <MicrophonePermission 
            onPermissionGranted={handleMicrophonePermissionGranted}
            onPermissionDenied={handleMicrophonePermissionDenied}
          />
        )}
        
        {/* Microphone Permission UI - Only show when waiting or denied */}
        {!isCorporateMode && showMicrophoneUI && (
          <MicrophonePermissionUI onHangup={handleQuestionHangup} />
        )}
        
        {/* Performance Monitor - only show in development */}
        {process.env.NODE_ENV === 'development' && <PerformanceMonitor show={true} onQuestionSequenceToggle={handleQuestionSequenceToggle} />}
        
        {/* Newspaper Modal */}
        <NewspaperModal 
          isOpen={newspaperModalOpen} 
          onClose={() => {
            // Track when modal closes for cooldown
            newspaperModalLastCloseRef.current = Date.now();
            closeNewspaperModal();
          }} 
        />
      </BackgroundWrapper>
      {/* Global mute indicator (bottom-left, positioned relative to uploading icon) */}
      {!isSoundOn && (
        <>
          <style>{`
            .mute-indicator {
              position: fixed;
              right: 16px;
              bottom: 16px;
              z-index: 1000;
              opacity: 0.9;
              pointer-events: auto;
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: url("/icons/pointer.svg") 10 0, pointer;
              transition: bottom 150ms ease;
            }
            /* Position relative to uploading icon when it's visible - mute icon on top (uploading icon is 34px + 8px gap = 42px above) */
            .mute-indicator.has-upload-icon {
              bottom: 66px;
            }
            @media (max-width: 859px) {
              .mute-indicator {
                bottom: auto;
                top: 92px;
                right: 20px;
              }
              .mute-indicator.has-upload-icon {
                bottom: auto;
                top: 92px;
                right: 20px;
              }
            }
          `}</style>
          <div
            role="button"
            tabIndex={0}
            aria-label="Sound is muted. Click to unmute."
            onClick={toggleSound}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggleSound();
              }
            }}
            className={`mute-indicator ${phoneboxState.isInQuestionMode ? 'has-upload-icon' : ''}`}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#ffffff' }}
            >
              <path d="M3 9v6h4l5 4V5L7 9H3z" fill="currentColor"/>
              <path d="M16 8l5 8M21 8l-5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        </>
      )}
       
      {/* Show preloader overlay during loading */}
      {isPreloading && <Preloader />}
      
      {/* Debug: Show canvas during preload if debugPreload=true in URL (dev mode only) */}
      {isPreloading && showCanvasDuringPreloadRef.current && showDebugMessageRef.current && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontFamily: 'monospace',
          fontSize: '14px',
          zIndex: 10001
        }}>
          <div style={{
            background: 'rgba(0, 0, 0, 0.8)',
            padding: '10px 20px',
            borderRadius: '4px'
          }}>
            Debug: Canvas visible during preload
          </div>
        </div>
      )}
    </>
  );
};

const App = () => {
  return (
    <MicrophoneProvider>
      <CanvasProvider>
        <SiteProvider>
          <AppContent />
        </SiteProvider>
      </CanvasProvider>
    </MicrophoneProvider>
  );
};

export default App;
